import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ChartBar, Users, TrendUp, Funnel, FilePdf, FileXls, ArrowRight,
  Phone, MapPin, Envelope, WhatsappLogo, ChatCircle, CaretLeft, CaretRight,
  WarningCircle, Target, CircleHalf, Lightning
} from "@phosphor-icons/react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell
} from "recharts";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";

const CHART_COLORS = ["#6366F1", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#38BDF8", "#F97316", "#14B8A6"];
const TYPE_COLORS = { visita: "#059669", llamada: "#2563eb", whatsapp: "#25D366", correo: "#7c3aed", demo: "#ea580c" };
const MONTHS = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

const SuggestionIcon = ({ type }) => {
  const icons = { visita: MapPin, llamada: Phone, whatsapp: WhatsappLogo, correo: Envelope };
  const Icon = icons[type] || ChatCircle;
  return <Icon size={14} weight="bold" />;
};

export const ComercialDashboardAdmin = () => {
  const [stats, setStats] = useState(null);
  const [statusTypes, setStatusTypes] = useState([]);
  const [period, setPeriod] = useState("monthly");
  const [loading, setLoading] = useState(true);
  const [intReport, setIntReport] = useState(null);
  const [intLoading, setIntLoading] = useState(true);
  const [pipeline, setPipeline] = useState(null);
  const { token } = useAuth();

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const [statsRes, statusRes, pipelineRes] = await Promise.all([
          axios.get(`${API}/comercial/stats/dashboard`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/comercial-status-types`),
          axios.get(`${API}/negociaciones/pipeline`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        setStats(statsRes.data);
        setStatusTypes(statusRes.data);
        setPipeline(pipelineRes.data);
      } catch { toast.error("Error al cargar estadisticas"); }
      finally { setLoading(false); }
    };
    fetchDashboard();
  }, [token]);

  const fetchInteractions = useCallback(async () => {
    setIntLoading(true);
    try {
      const res = await axios.get(`${API}/comercial/reports/interactions?year=${year}&month=${month}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setIntReport(res.data);
    } catch { /* silent */ }
    finally { setIntLoading(false); }
  }, [token, year, month]);

  useEffect(() => { fetchInteractions(); }, [fetchInteractions]);

  const prevMonth = () => { if (month === 1) { setMonth(12); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 12) { setMonth(1); setYear(y => y + 1); } else setMonth(m => m + 1); };

  const getStatusColor = (statusName) => {
    const st = statusTypes.find(s => s.name === statusName);
    return st?.hex_color || "#64748B";
  };

  const handleExport = async (type) => {
    try {
      const endpoint = type === "pdf" ? "/comercial/export/pdf" : "/comercial/export/excel";
      const res = await axios.get(`${API}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob"
      });
      const ext = type === "pdf" ? "pdf" : "xlsx";
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `reporte_comercial_${new Date().toISOString().slice(0, 10)}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success(`${type.toUpperCase()} descargado`);
    } catch { toast.error(`Error al exportar ${type.toUpperCase()}`); }
  };

  if (loading) return <div className="text-center py-8 text-slate-400">Cargando dashboard...</div>;
  if (!stats) return null;

  const trendData = period === "monthly" ? stats.monthly_data : stats.weekly_data;
  const funnel = stats.funnel;
  const totalContacts = stats.total_contacts;
  const conversionRate = totalContacts > 0 ? ((funnel.clientes / totalContacts) * 100).toFixed(1) : 0;
  const funnelStages = stats.funnel_stages || [];

  const coveragePct = intReport && intReport.total_contacts > 0
    ? ((intReport.total_contacted / intReport.total_contacts) * 100).toFixed(1) : 0;

  return (
    <div className="space-y-6" data-testid="comercial-dashboard-admin">
      {/* Header + Export */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Dashboard Comercial</h1>
          <p className="text-xs text-slate-400 mt-0.5">Vista unificada de contactos, prospeccion e interacciones</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => handleExport("excel")} className="text-emerald-700 border-emerald-300 hover:bg-emerald-50" data-testid="export-comercial-excel">
            <FileXls size={18} className="mr-1.5" /> Excel
          </Button>
          <Button variant="outline" onClick={() => handleExport("pdf")} className="text-red-700 border-red-300 hover:bg-red-50" data-testid="export-comercial-pdf">
            <FilePdf size={18} className="mr-1.5" /> PDF
          </Button>
        </div>
      </div>

      {/* === SECTION 1: KPIs === */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="kpi-cards">
        <KpiCard icon={Users} color="slate" label="Total Contactos" value={totalContacts} testId="kpi-total" />
        <KpiCard icon={Funnel} color="violet" label="Leads" value={funnel.leads} testId="kpi-leads" />
        <KpiCard icon={TrendUp} color="amber" label="Prospectos" value={funnel.prospectos} testId="kpi-prospectos" />
        <KpiCard icon={Target} color="emerald" label="Clientes" value={funnel.clientes} sub={`${conversionRate}% conv.`} testId="kpi-clientes" />
        <KpiCard icon={Lightning} color="indigo" label="Interacciones/Mes" value={intReport?.total_interactions || 0} testId="kpi-interactions" />
        <KpiCard icon={CircleHalf} color="cyan" label="Cobertura" value={`${coveragePct}%`} sub={`${intReport?.total_contacted || 0} de ${intReport?.total_contacts || 0}`} testId="kpi-coverage" />
      </div>

      {/* === SECTION 2: Funnel Dinamico === */}
      {funnelStages.length > 0 && (
        <div className="bg-white border border-slate-200 p-5 rounded-xl" data-testid="conversion-funnel">
          <h3 className="text-xs font-semibold text-slate-500 mb-4 uppercase tracking-wider">Pipeline Comercial por Estatus</h3>
          <div className="flex items-stretch gap-1.5 overflow-x-auto pb-2">
            {funnelStages.map((stage, i) => (
              <div key={stage.key} className="flex items-center gap-1.5 flex-1 min-w-0">
                <div className="flex-1 text-center py-3 px-2 rounded-lg border" style={{ borderColor: `${stage.color}50`, backgroundColor: `${stage.color}0D` }}>
                  <p className="text-xl font-bold" style={{ color: stage.color }}>{stage.count}</p>
                  <p className="text-[10px] font-medium truncate" style={{ color: stage.color }}>{stage.label}</p>
                </div>
                {i < funnelStages.length - 1 && <ArrowRight size={16} className="text-slate-300 flex-shrink-0" />}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* === PIPELINE NEGOCIACIONES === */}
      {pipeline && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Funnel Chart */}
          <div className="bg-white border border-slate-200 p-5 rounded-xl" data-testid="pipeline-funnel">
            <h3 className="text-xs font-semibold text-slate-500 mb-4 uppercase tracking-wider">Pipeline Negociaciones</h3>
            <div className="space-y-2">
              {pipeline.by_stage.map((s, i) => {
                const maxTotal = Math.max(...pipeline.by_stage.map(x => x.total), 1);
                const pct = Math.max((s.total / maxTotal) * 100, 3);
                return (
                  <div key={s.id} className="flex items-center gap-3">
                    <span className="text-[10px] font-semibold text-slate-600 w-24 text-right shrink-0">{s.name}</span>
                    <div className="flex-1 bg-slate-100 rounded-full h-7 relative overflow-hidden">
                      <div className="h-full rounded-full flex items-center justify-end px-2 transition-all" style={{ width: `${pct}%`, backgroundColor: s.color }}>
                        <span className="text-[10px] font-bold text-white">${s.total.toLocaleString("es-MX")}</span>
                      </div>
                    </div>
                    <span className="text-[10px] text-slate-400 w-8 shrink-0">{s.count}</span>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 pt-3 border-t text-center">
              <p className="text-2xl font-extrabold text-slate-900">${pipeline.grand_total.toLocaleString("es-MX")}</p>
              <p className="text-[10px] text-slate-400">Total en pipeline ({pipeline.total_count} negociaciones)</p>
            </div>
          </div>

          {/* Monthly Trend + Sellers */}
          <div className="bg-white border border-slate-200 p-5 rounded-xl" data-testid="pipeline-monthly">
            <h3 className="text-xs font-semibold text-slate-500 mb-4 uppercase tracking-wider">Tendencia Mensual</h3>
            {pipeline.by_month.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={pipeline.by_month}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v) => `$${v.toLocaleString("es-MX")}`} />
                  <Bar dataKey="total" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-sm text-slate-400 text-center py-10">Sin datos mensuales</p>}

            {pipeline.by_seller.length > 0 && (
              <div className="mt-4 pt-3 border-t">
                <p className="text-[10px] font-semibold text-slate-500 uppercase mb-2">Por vendedor</p>
                {pipeline.by_seller.slice(0, 5).map(v => (
                  <div key={v.id} className="flex items-center justify-between py-1.5 text-xs">
                    <span className="font-semibold text-slate-700">{v.name}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-slate-400">{v.count} neg.</span>
                      <span className="font-bold text-slate-900">${v.total.toLocaleString("es-MX")}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* === SECTION 3: Charts Row 1 === */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Trend Chart */}
        <div className="bg-white border border-slate-200 p-5 rounded-xl" data-testid="trend-chart">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Altas y Conversiones</h3>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-32 h-7 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">Por Mes</SelectItem>
                <SelectItem value="weekly">Por Semana</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey={period === "monthly" ? "month" : "week"} tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip contentStyle={{ fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="altas" name="Altas" fill="#6366F1" radius={[3, 3, 0, 0]} />
              <Bar dataKey="prospectos" name="Prospectos" fill="#F59E0B" radius={[3, 3, 0, 0]} />
              <Bar dataKey="clientes" name="Clientes" fill="#10B981" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Status Pie Chart */}
        <div className="bg-white border border-slate-200 p-5 rounded-xl" data-testid="status-pie-chart">
          <h3 className="text-xs font-semibold text-slate-500 mb-4 uppercase tracking-wider">Distribucion por Estatus</h3>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={stats.status_breakdown.filter(s => s.count > 0)}
                cx="50%" cy="50%"
                outerRadius={90} innerRadius={40}
                dataKey="count" nameKey="label"
                label={({ label, count }) => `${label}: ${count}`}
                labelLine={{ strokeWidth: 1 }}
              >
                {stats.status_breakdown.filter(s => s.count > 0).map((entry, i) => (
                  <Cell key={i} fill={getStatusColor(entry.status) || CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* === SECTION 4: Interaction Analytics (with month nav) === */}
      <div className="bg-white border border-indigo-100 rounded-xl p-5" data-testid="interaction-analytics-section">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-5">
          <h3 className="text-xs font-semibold text-indigo-600 uppercase tracking-wider flex items-center gap-2">
            <Lightning size={16} weight="bold" /> Analiticas de Prospeccion
          </h3>
          <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-lg px-2 py-1" data-testid="report-month-nav">
            <button onClick={prevMonth} className="p-0.5 hover:bg-indigo-100 rounded"><CaretLeft size={16} /></button>
            <span className="text-xs font-semibold text-indigo-700 min-w-[110px] text-center">{MONTHS[month - 1]} {year}</span>
            <button onClick={nextMonth} className="p-0.5 hover:bg-indigo-100 rounded"><CaretRight size={16} /></button>
          </div>
        </div>

        {intLoading ? (
          <div className="text-center py-8 text-slate-400 text-sm">Cargando interacciones...</div>
        ) : intReport ? (
          <div className="space-y-5">
            {/* Interaction Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {intReport.by_type.length > 0 && (
                <div className="border border-slate-100 rounded-lg p-4" data-testid="int-by-type-chart">
                  <h4 className="text-xs font-semibold text-slate-600 mb-3">Interacciones por Tipo</h4>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={intReport.by_type} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis type="number" tick={{ fontSize: 10 }} />
                      <YAxis dataKey="label" type="category" width={110} tick={{ fontSize: 11 }} />
                      <Tooltip contentStyle={{ fontSize: 12 }} />
                      <Bar dataKey="count" name="Cantidad" radius={[0, 4, 4, 0]}>
                        {intReport.by_type.map((entry, i) => (
                          <Cell key={i} fill={TYPE_COLORS[entry.type] || CHART_COLORS[i % CHART_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
              {intReport.by_user.length > 0 && (
                <div className="border border-slate-100 rounded-lg p-4" data-testid="int-by-user-chart">
                  <h4 className="text-xs font-semibold text-slate-600 mb-3">Interacciones por Asesor</h4>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={intReport.by_user}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="user_name" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip contentStyle={{ fontSize: 12 }} />
                      <Bar dataKey="count" name="Interacciones" fill="#6366F1" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* Contacted Table */}
            {intReport.contacted.length > 0 && (
              <div className="border border-slate-100 rounded-lg p-4" data-testid="contacted-table">
                <h4 className="text-xs font-semibold text-emerald-700 mb-3 flex items-center gap-1.5">
                  <Users size={15} /> Contactos con Mayor Actividad
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-1.5 px-2 text-[10px] text-slate-500 font-medium uppercase">Empresa</th>
                        <th className="text-left py-1.5 px-2 text-[10px] text-slate-500 font-medium uppercase">Contacto</th>
                        <th className="text-left py-1.5 px-2 text-[10px] text-slate-500 font-medium uppercase">Asesor</th>
                        <th className="text-center py-1.5 px-2 text-[10px] text-slate-500 font-medium uppercase">Total</th>
                        <th className="text-left py-1.5 px-2 text-[10px] text-slate-500 font-medium uppercase">Desglose</th>
                        <th className="text-left py-1.5 px-2 text-[10px] text-slate-500 font-medium uppercase">Ultimo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {intReport.contacted.slice(0, 10).map((c, i) => (
                        <tr key={c.id || i} className="border-b border-slate-50 hover:bg-slate-50">
                          <td className="py-1.5 px-2 font-medium text-slate-800 text-xs">{c.company_name}</td>
                          <td className="py-1.5 px-2 text-slate-600 text-xs">{c.contact_name}</td>
                          <td className="py-1.5 px-2 text-amber-700 text-xs">{c.assigned_to_name || "-"}</td>
                          <td className="py-1.5 px-2 text-center">
                            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 font-bold text-xs">{c.total_interactions}</span>
                          </td>
                          <td className="py-1.5 px-2">
                            <div className="flex gap-1 flex-wrap">
                              {Object.entries(c.by_type || {}).map(([type, count]) => (
                                <span key={type} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium"
                                  style={{ backgroundColor: `${TYPE_COLORS[type] || '#64748b'}15`, color: TYPE_COLORS[type] || '#64748b' }}>
                                  {count} {type}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="py-1.5 px-2 text-[10px] text-slate-500 font-mono">{c.last_date || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Not Contacted Table */}
            {intReport.not_contacted.length > 0 && (
              <div className="border border-amber-200 bg-amber-50/50 rounded-lg p-4" data-testid="not-contacted-table">
                <h4 className="text-xs font-semibold text-amber-800 mb-1 flex items-center gap-1.5">
                  <WarningCircle size={15} weight="fill" className="text-amber-500" /> Sin Interaccion en {MONTHS[month - 1]} ({intReport.not_contacted.length})
                </h4>
                <p className="text-[10px] text-amber-600 mb-3">Sugerencias de seguimiento basadas en tiempo sin contacto.</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-amber-200">
                        <th className="text-left py-1.5 px-2 text-[10px] text-amber-700 font-medium uppercase">Empresa</th>
                        <th className="text-left py-1.5 px-2 text-[10px] text-amber-700 font-medium uppercase">Contacto</th>
                        <th className="text-left py-1.5 px-2 text-[10px] text-amber-700 font-medium uppercase">Telefono</th>
                        <th className="text-left py-1.5 px-2 text-[10px] text-amber-700 font-medium uppercase">Correo</th>
                        <th className="text-center py-1.5 px-2 text-[10px] text-amber-700 font-medium uppercase">Dias</th>
                        <th className="text-center py-1.5 px-2 text-[10px] text-amber-700 font-medium uppercase">Sugerencia</th>
                      </tr>
                    </thead>
                    <tbody>
                      {intReport.not_contacted.slice(0, 15).map((c, i) => (
                        <tr key={c.id || i} className="border-b border-amber-100 hover:bg-amber-50">
                          <td className="py-1.5 px-2 font-medium text-slate-800 text-xs">{c.company_name}</td>
                          <td className="py-1.5 px-2 text-slate-600 text-xs">{c.contact_name}</td>
                          <td className="py-1.5 px-2 text-slate-500 text-[10px]">{c.phone || "-"}</td>
                          <td className="py-1.5 px-2 text-slate-500 text-[10px]">{c.email || "-"}</td>
                          <td className="py-1.5 px-2 text-center">
                            {c.days_since != null ? (
                              <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${c.days_since > 30 ? 'bg-red-100 text-red-700' : c.days_since > 14 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                                {c.days_since}d
                              </span>
                            ) : <span className="text-[10px] text-slate-400">Nunca</span>}
                          </td>
                          <td className="py-1.5 px-2 text-center">
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium"
                              style={{ backgroundColor: `${TYPE_COLORS[c.suggestion] || '#64748b'}12`, color: TYPE_COLORS[c.suggestion] || '#64748b' }}>
                              <SuggestionIcon type={c.suggestion} /> {c.suggestion}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {intReport.total_interactions === 0 && intReport.not_contacted.length === 0 && (
              <div className="text-center py-8 text-slate-400 text-sm border border-dashed border-slate-200 rounded-lg">
                No hay datos de interacciones para {MONTHS[month - 1]} {year}.
              </div>
            )}
          </div>
        ) : null}
      </div>

      {/* === SECTION 5: Sellers Table === */}
      <div className="bg-white border border-slate-200 p-5 rounded-xl" data-testid="sellers-table">
        <h3 className="text-xs font-semibold text-slate-500 mb-4 uppercase tracking-wider">Rendimiento por Vendedor</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-2 px-3 text-[10px] text-slate-500 font-medium uppercase">Vendedor</th>
                <th className="text-center py-2 px-3 text-[10px] text-slate-500 font-medium uppercase">Total</th>
                <th className="text-center py-2 px-3 text-[10px] text-slate-500 font-medium uppercase">Prospectos</th>
                <th className="text-center py-2 px-3 text-[10px] text-slate-500 font-medium uppercase">Clientes</th>
                <th className="text-center py-2 px-3 text-[10px] text-slate-500 font-medium uppercase">% Conversion</th>
              </tr>
            </thead>
            <tbody>
              {stats.seller_stats.map((s, i) => (
                <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                  <td className="py-2 px-3 font-medium text-slate-800">{s.seller_name}</td>
                  <td className="py-2 px-3 text-center text-slate-700 font-semibold">{s.total}</td>
                  <td className="py-2 px-3 text-center text-amber-600 font-medium">{s.prospectos}</td>
                  <td className="py-2 px-3 text-center text-emerald-600 font-medium">{s.clientes}</td>
                  <td className="py-2 px-3 text-center">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${s.conversion_rate >= 30 ? 'bg-emerald-100 text-emerald-700' : s.conversion_rate >= 15 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                      {s.conversion_rate}%
                    </span>
                  </td>
                </tr>
              ))}
              {stats.seller_stats.length === 0 && (
                <tr><td colSpan={5} className="text-center py-6 text-slate-400 text-sm">Sin datos de vendedores</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* === SECTION 6: City Distribution === */}
      {stats.city_stats.length > 0 && (
        <div className="bg-white border border-slate-200 p-5 rounded-xl" data-testid="city-chart">
          <h3 className="text-xs font-semibold text-slate-500 mb-4 uppercase tracking-wider">Contactos por Ciudad</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={stats.city_stats} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
              <YAxis type="category" dataKey="city" tick={{ fontSize: 10 }} width={90} />
              <Tooltip contentStyle={{ fontSize: 12 }} />
              <Bar dataKey="count" name="Contactos" fill="#6366F1" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

function KpiCard({ icon: Icon, color, label, value, sub, testId }) {
  const colors = {
    slate: { bg: "bg-slate-50", border: "border-slate-200", icon: "text-slate-400", value: "text-slate-900" },
    violet: { bg: "bg-violet-50", border: "border-violet-200", icon: "text-violet-400", value: "text-violet-700" },
    amber: { bg: "bg-amber-50", border: "border-amber-200", icon: "text-amber-400", value: "text-amber-700" },
    emerald: { bg: "bg-emerald-50", border: "border-emerald-200", icon: "text-emerald-400", value: "text-emerald-700" },
    indigo: { bg: "bg-indigo-50", border: "border-indigo-200", icon: "text-indigo-400", value: "text-indigo-700" },
    cyan: { bg: "bg-cyan-50", border: "border-cyan-200", icon: "text-cyan-400", value: "text-cyan-700" },
  };
  const c = colors[color] || colors.slate;
  return (
    <div className={`${c.bg} border ${c.border} p-4 rounded-xl`} data-testid={testId}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon size={16} className={c.icon} weight="duotone" />
        <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{label}</span>
      </div>
      <p className={`text-2xl font-bold ${c.value}`}>{value}</p>
      {sub && <p className="text-[10px] text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
}
